import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link
} from '@mui/material';
import {
  ArrowBack,
  Email,
  Phone,
  LocationOn,
  Send,
  ContactSupport
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ContactUsScreen = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    setTimeout(() => {
      setSubmitted(true);
      setLoading(false);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1000);
  };

  const contactMethods = [
    {
      icon: <Email />,
      primary: 'Email Support',
      secondary: 'contactus@bettoradvantage.com',
      action: () => window.open('mailto:contactus@bettoradvantage.com')
    }
  ];

  return (
    <Box sx={{ py: 2, px: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/account')}
          sx={{
            mr: 2
          }}
        >
          Back
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Contact Us
        </Typography>
      </Box>

      {/* Success Alert */}
      {submitted && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          onClose={() => setSubmitted(false)}
        >
          Thank you for your message! We'll get back to you within 24 hours.
        </Alert>
      )}

      {/* Contact Methods */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <ContactSupport sx={{ mr: 1 }} />
            Get in Touch
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We're here to help! Choose how you'd like to contact us.
          </Typography>
          
          <List sx={{ py: 0 }}>
            {contactMethods.map((method, index) => (
              <React.Fragment key={index}>
                <ListItem
                  button
                  onClick={method.action}
                  sx={{ py: 2, px: 0 }}
                >
                  <ListItemIcon>
                    {method.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={method.primary}
                    secondary={method.secondary}
                  />
                </ListItem>
                {index < contactMethods.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Contact Form */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Send us a Message
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Fill out the form below and we'll respond as soon as possible.
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Message"
              name="message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleInputChange}
              required
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              startIcon={<Send />}
              fullWidth
              disabled={loading}
              sx={{ py: 1.5 }}
            >
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ContactUsScreen;